import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Alert, TextField, Button } from '@mui/material';
import { BLOCKED_EMAIL_DOMAINS } from '../../../../shared/constants/blockedEmailDomains';
import { PhoneNumberInput } from '@nx/common-ui-components/components';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { countryCodes } from '../../../../shared/constants/countryCodes';
import { useSurveyContent } from '../../../../shared/hooks';
import { useFinalizeCustomBlock, useHandleSubmit } from './hooks';
import AlertComp from '../../../_shared/alert';
import { ROUTES } from 'shared/routes/constants';
import { Link } from 'react-router-dom';

export const ContactInformationForm = (): JSX.Element => {
  const { surveyContent } = useSurveyContent();
  const handleSubmit = useHandleSubmit();
  const handleFinishCustomBlock = useFinalizeCustomBlock();
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [countryCode, setCountryCode] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [finishedPosting, setFinishedPosting] = useState(false);
  const [email, setEmail] = React.useState('');
  const emailDomain = email.split('@')[1] || '';
  const consultationMethod = surveyContent?.consultation_method;
  const isBlockedDomain =
    BLOCKED_EMAIL_DOMAINS.includes(emailDomain) || emailDomain.endsWith('.edu');

  useEffect(() => {
    if (consultationMethod === 'ai_moderated') {
      setFinishedPosting(true);
    }
  }, [consultationMethod]);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !isBlockedDomain;

  const showEmailError =
    email === '' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || isBlockedDomain;

  const phoneErrorList = [
    {
      condition: phoneNumber !== '' && !/^[-+()\d\s]+$/.test(phoneNumber),
      message: 'Invalid phone number',
    },
    {
      condition:
        phoneNumber !== '' &&
        countryCode === '+1' &&
        !/^(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(phoneNumber),
      message: 'Invalid US phone number',
    },
  ];
  const isDisabled =
    !firstName ||
    !lastName ||
    !email ||
    !phoneNumber ||
    !countryCode ||
    !isValidEmail ||
    phoneErrorList.find((item) => item.condition) !== undefined;
  const sortedCountryCodes = [
    ...countryCodes.filter((c) => c.label === 'United States'),
    ...countryCodes
      .filter((c) => c.label !== 'United States')
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const [touched, setTouched] = React.useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  const phoneInputWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [isPhoneFocused, setIsPhoneFocused] = React.useState(false);

  const finishContactInfo = async () => {
    const data = {
      id: surveyContent?.subject ?? '',
      first_name: firstName || '',
      last_name: lastName || '',
      email: email || '',
      phone_number: (countryCode || '') + (phoneNumber || ''),
    };
    const fullUrl = window.location.href;
    await handleFinishCustomBlock(fullUrl, true);
    await handleSubmit(data, setShowAlert, setFinishedPosting);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        phoneInputWrapperRef.current &&
        !phoneInputWrapperRef.current.contains(event.target as Node)
      ) {
        setTouched((prev) => ({ ...prev, phone: true }));
        setIsPhoneFocused(false);
      }
    };

    if (isPhoneFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPhoneFocused]);

  useEffect(() => {
    if (finishedPosting) {
      const urlAux = window.location.href.replace('/i/', '/c/');
      window.location.assign(urlAux);
    }
  }, [finishedPosting]);

  return (
    <Box sx={{ px: 2, py: 0, width: '100%', maxWidth: 500, mx: 'auto' }}>
      <Card sx={{ width: '100%', borderRadius: '16px' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Alert
            severity="success"
            icon={<CelebrationOutlinedIcon sx={{ fontSize: '20px' }} />}
            sx={{
              bgcolor: '#e6f5eb',
              border: '1px solid #c3e5cd',
              color: '#065b2c',
              py: '0px !important',
              width: 'fit-content',
              mt: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: '#065b2c', fontSize: '11px' }}>
              You’re Almost there!
            </Typography>
          </Alert>
          {showAlert && (
            <Box sx={{ position: 'fixed', top: '16px', zIndex: 999999 }}>
              <AlertComp
                severity="error"
                open={showAlert}
                onClose={() => setShowAlert(false)}
                title="Sorry, there was an error submitting your availability"
                message="Please try again."
              />
            </Box>
          )}
          <Box>
            <Typography variant="h3" fontWeight="bold" marginBottom={'16px'}>
              Contact Information
            </Typography>
            <Typography variant="body2" sx={{ color: '#384151' }}>
              Please provide your preferred contact information details. We will use these to send
              your consultation invitation if selected
            </Typography>
          </Box>

          <TextField
            label="First Name"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, firstName: true }))}
            placeholder="First Name"
            fullWidth
            error={touched.firstName && firstName === ''}
            helperText={touched.firstName && firstName === '' ? 'Required field' : ''}
          />

          <TextField
            label="Last Name"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, lastName: true }))}
            placeholder="Last Name"
            fullWidth
            error={touched.lastName && lastName === ''}
            helperText={touched.lastName && lastName === '' ? 'Required field' : ''}
          />

          <TextField
            label="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
            placeholder="Personal Email"
            fullWidth
            error={touched.email && showEmailError}
            helperText={touched.email && showEmailError ? 'Enter a valid email address' : ''}
          />
          <div
            ref={phoneInputWrapperRef}
            onFocus={() => setIsPhoneFocused(true)}
            onBlur={() => setIsPhoneFocused(false)}
          >
            <PhoneNumberInput
              label="Phone number *"
              onPhoneChange={(value) => setPhoneNumber(value)}
              onCountryChange={(value) => {
                setCountryCode(value);
                setTouched((prev) => ({ ...prev, phone: false }));
              }}
              errors={touched.phone ? phoneErrorList : []}
              countryCode={countryCode}
              phone={phoneNumber}
              countryOptions={sortedCountryCodes}
            />
          </div>
        </CardContent>
      </Card>
      <Box
        sx={{
          mt: 2,
          px: 2,
          py: 1,
          backgroundColor: '#eef6ff',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          border: '1px solid #c6d9ec',
        }}
      >
        <AdminPanelSettingsOutlinedIcon />
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            Your Privacy Matters
          </Typography>
          <Typography variant="body2" sx={{ color: '#384151' }}>
            We take your privacy seriously. Your contact information will never be shared with third
            parties.
          </Typography>
          <Typography
            variant="body2"
            component={Link}
            to={ROUTES.SURVEY.PATHS.TERMS}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: '#005FCC', textDecoration: 'underline', fontWeight: 500 }}
          >
            Privacy Policy
          </Typography>
        </Box>
      </Box>

      <Button
        variant="contained"
        color="primary"
        disabled={isDisabled}
        fullWidth
        onClick={finishContactInfo}
        sx={{ mt: 3, borderRadius: '8px' }}
      >
        SUBMIT
      </Button>
    </Box>
  );
};

export default ContactInformationForm;
